<template>

    <div :class="{
             'active' : elementActive,
             'marked-for-deletion': elementMarkedForDeletion
         }"
         :data-id="inner_element.id"
         class="publish-element"
         @click="setActive()"
         @mouseleave="elementHover = false"
         @mouseover="elementHover = true">

        <advanced-thumbnail
            :alt="`Preview of ${element.data.name}`"
            :aspectRatio="aspectRatio"
            :href="element.href"
            :margin="false"
            :previewHeight="elementHeight"
            :grid="grid"
            :style="thumbnailStyle"
            @click="showMedia()">
            <element-preview-v2
                :aspectRatio="aspectRatio"
                :data="element.data"
                :layer-height="active_layer.height"
                :layer-width="active_layer.width"
                :previewHeight="elementHeight"
                :show-element-icon="false"
                :show-element-state="false"
                :size-ratio="sizeRatio"/>

            <div class="video-overlay overlay-fullsize" v-if="element.data.type === 'video'">
                <div class="wrap text-center">
                    <i class="fa-regular fa-play fa-3x text-primary" :style="{'fontSize': Math.max(min_width_height/4, 22) + 'px'}"></i>
                </div>
            </div>
        </advanced-thumbnail>

        <div :class="{
            'publish-element-content-container-four-rows': elementHeight === 128,
            'publish-element-content-container-two-rows': elementHeight === 60,

        }"
             class="publish-element-content-container">
            <div class="publish-element-title-row">

                <div
                    :class="{
                        'publish-element-readonly': readonly,
                        'publish-element-sizenotmatching': sizeNotMatching || layerNotSet || propsNotSet,
                        'publish-element-linked': inner_element.data.state === 'link',
                        'publish-element-copied': inner_element.data.state === 'copy',
                    }"
                    class="publish-element-title-background">

                    <elementIcon :container-classes="['element-icon-container-gap']"
                                 :data="inner_element.data">
                        <size-not-matching-icon v-if="sizeNotMatching"/>
                        <warning-icon v-if="propsNotSet" :text="t_props_not_set"/>

                        <v-tooltip-wrapper v-if="readonly"
                                           :max-width="380"
                                           :nudge-bottom="2"
                                           :open-delay="350"
                                           content-class="tooltip-glass tooltip-white elevation-8 tooltip-border-colored tooltip-border-readonly">
                            <template #activator="{ on, attrs }">
                                <i class="far fa-lock-keyhole color-readonly-icon"
                                   v-bind="attrs"
                                   v-on="on"/>
                            </template>
                            <span><b>{{ t_readonly }}</b></span><br/>
                            <span>{{ t_screen_not_allowed }}</span>
                        </v-tooltip-wrapper>
                    </elementIcon>

                    <text-ellipsis-with-tooltip :text="elementName"/>

                </div>

                <v-spacer/>

                <actions v-if="elementHover || elementActive || elementDeletionInProgress"
                         :delete-privilege="deletePrivilege"
                         :edit-privilege="editPrivilege"
                         :element="inner_element"
                         size="small"
                         :loading-deletion-state="elementDeletionInProgress"
                         :delete-disabled="loadingDeletionState"
                         @convertLink="$emit('convertLink')"
                         @createCopy="$emit('createCopy')"
                         @redirectTo="$emit('redirectTo')"
                         @softDelete="$emit('softDelete')"
                         @unDelete="$emit('unDelete')"/>

                <element-state :state="inner_state"
                               colored-shadow
                               show-tooltip/>
            </div>

            <div class="publish-element-info-container">

                <div style="gap: 4px; display: flex; flex-wrap: wrap;">
                    <v-chip class="elevation-1"
                            label
                            small>
                        <i class="far fa-timer"/>
                        &nbsp;
                        {{ elementDuration }} {{ elementLoops }}
                    </v-chip>

                    <validity-display chip noTime :element="element.data" />

                    <v-chip class="elevation-1"
                            label
                            small>
                        <i class="far fa-clock"/>
                        &nbsp;
                        {{ validityTime }}
                    </v-chip>

                    <v-chip v-if="validityWeekdays"
                            class="elevation-1"
                            label
                            small>
                        <i class="far fa-calendar-week"/>
                        &nbsp;
                        {{ validityWeekdays }}
                    </v-chip>

                    <v-chip
                            class="elevation-1 publish-element-description-chip"
                            label
                            small>
                        <div class="publish-element-description-container">
                            <i class="far fa-circle-info"/>
                            <div class="publish-element-description">
                                {{ getDescriptionForElement(inner_element.data) }}
                            </div>
                        </div>
                    </v-chip>
                </div>

                <div v-if="elementHeight !== 60" class="publish-element-info-approval-switch">
                    <span :class="{
                            'switch-label-clickable': !approveSwitchDisabled,
                            'switch-label-invisible': elementMarkedForDeletion || approved
                        }"
                              class="publish-element-switch-label"
                              @[approveSwitchDisabled?null:`click`]="approved = !approved">
                            {{ !approved ? t_not_approved : t_approved }}
                    </span>

                    <v-switch
                        v-if="canBeApproved"
                        v-model="approved"
                        style="margin-top: 0; padding-top: 0;"
                        :class="{'switch-invisible': elementMarkedForDeletion}"
                        :aria-label="approveSwitchAriaLabel"
                        :disabled="approveSwitchDisabled"
                        dense hide-details inset
                    />
                </div>
            </div>

            <div class="publish-element-screens-container">
                <template
                    v-for="({text, icon}, index) in cappedNamedElementScreensAndScreengroups">
                    <v-chip :key="index"
                            class="elevation-1 mr-1"
                            color="primary"
                            label
                            small
                            @[readonly?null:`click`]="scrollToScreenSettings(); showScreensModal()">
                        <i :class="icon"
                           class="far"/>
                        &nbsp;
                        {{ text }}
                    </v-chip>
                </template>

                <v-tooltip-wrapper v-if="noScreensOrScreengroupsAssigned"
                                   :max-width="420"
                                   :open-delay="150"
                                   content-class="tooltip-glass tooltip-white elevation-8 tooltip-border-colored tooltip-border-error"
                                   tooltip-position="bottom">
                    <template #activator="{ on, attrs }">
                        <v-chip
                            class="publish-element-screen-tag-background elevation-1"
                            color="#ab1900"
                            label
                            outlined
                            small
                            v-bind="attrs"
                            v-on="on"
                            @[readonly?null:`click`]="scrollToScreenSettings(); showScreensModal()">
                            <i class="far fa-display-slash"/>
                            &nbsp;
                            {{ t_screen_screengroup_missing }}
                        </v-chip>
                    </template>
                    {{ t_element_error_info() }}
                </v-tooltip-wrapper>

                <v-tooltip-wrapper v-if="showOtherScreensChip"
                                   :max-width="280"
                                   :nudge-bottom="8"
                                   content-class="tooltip-glass tooltip-white elevation-8 tooltip-less-padding tooltip-more-rounded tooltip-op-42 "
                                   tooltip-position="bottom">
                    <template #activator="{ on, attrs }">
                        <v-chip
                            class="elevation-1 mr-1"
                            color="primary"
                            label
                            small
                            v-bind="attrs"
                            v-on="on"
                            @[readonly?null:`click`]="scrollToScreenSettings(); showScreensModal()">
                            <i class="far fa-display-chart-up publish-element-screen-icon-semi-transparent"/>
                            &nbsp;
                            +{{ numOtherScreensOrScreengroupsAssigned }} {{ t_others }}
                        </v-chip>
                    </template>
                    <div class="publish-element-tooltip-other-screens-container">
                        <template v-for="({text, icon}, index) in cappedRestNamedElementScreensAndScreengroups">
                            <v-chip :key="index"
                                    class="elevation-1 mr-1"
                                    color="primary"
                                    label
                                    small>
                                <span :key="index"
                                      class="publish-element-tooltip-other-screens-row">
                                    <i :class="icon"
                                       class="far" />
                                    <span>{{ text }}</span>
                                </span>
                            </v-chip>
                        </template>

                        <template v-if="numOfMoreScreensOrScreengroups > 0">
                            <v-chip
                                class="elevation-1 mr-1"
                                color="primary"
                                label
                                small>
                                <span class="publish-element-tooltip-other-screens-row">
                                    <i class="far fa-display-chart-up publish-element-screen-icon-semi-transparent" />
                                    <span>+{{ numOfMoreScreensOrScreengroups }} {{ t_more }}</span>
                                </span>
                            </v-chip>
                        </template>
                    </div>
                </v-tooltip-wrapper>

                <v-chip
                    class="elevation-1"
                    label
                    small
                    @[readonly?null:`click`]="scrollToScreenSettings(); showScreensModal()">
                    <i class="far fa-plus"/>
                </v-chip>
            </div>

            <div class="publish-element-placeholder-row" :class="{'placeholder-flex-grow': elementHeight === 60}"></div>

            <div v-if="elementHeight === 60" class="publish-element-info-approval-switch">
                <span :class="{
                        'switch-label-clickable': !approveSwitchDisabled,
                        'switch-label-invisible': elementMarkedForDeletion || approved
                    }"
                      class="publish-element-switch-label"
                      @[approveSwitchDisabled?null:`click`]="approved = !approved">
                        {{ !approved ? t_not_approved : t_approved }}
                </span>

                <v-switch
                    v-if="canBeApproved"
                    v-model="approved"
                    style="margin-top: 0; padding-top: 0;"
                    :class="{'switch-invisible': elementMarkedForDeletion}"
                    :aria-label="approveSwitchAriaLabel"
                    :disabled="approveSwitchDisabled"
                    dense hide-details inset
                />
            </div>
        </div>
    </div>
</template>

<script>
import bootstrapElement
    from './_bootstrapElement';
import actions
    from './actions';
import utility
    , {
    checkValidity
} from '../../../utility';

import textElement
    from '../elements/text.vue';
import rssfeedElement
    from '../elements/rssfeed.vue';
import iframeElement
    from '../elements/iframe.vue';
import youtubeElement
    from '../elements/youtube.vue';
import qrcodeElement
    from '../elements/qrcode.vue';
import countdownElement
    from '../elements/countdown.vue';
import pictureElement
    from '../elements/picture.vue';
import videoElement
    from '../elements/video.vue';
import pdfElement
    from '../elements/pdf.vue';
import pptElement
    from '../elements/ppt.vue';
import emptyrectangleElement
    from '../elements/emptyrectangle.vue';
import templateElement
    from '../elements/template.vue';
import Template
    from '../elements/template.vue';
import campaignElement
    from '../elements/campaign.vue';
import clockElement
    from '../elements/clock.vue';
import dayElement
    from '../elements/day.vue';
import weatherElement
    from '../elements/weather.vue';
import layerElement
    from '../elements/layer.vue';
import esignElement
    from '../elements/esign.vue';
import streamingElement
    from '../elements/streaming.vue';
import elementState
    from './elementstate.vue';
import widgetElement
    from '../elements/widget';
import elementTypes
    from '../elements/types';
import elementIcon
    from '../elements/element-icon';
import AdvancedThumbnail
    from '../common/advanced-thumbnail.vue';
import TextEllipsisWithTooltip
    from '../common/TextEllipsisWithTooltip.vue';
import SizeNotMatchingIcon
    from '../elements/sizeNotMatchingIcon.vue';
import {
    elementSizeDifferentFromLayerSize, getDescriptionForElement
} from '../../../../utils/publish';
import {
    t_element_error_info
} from '../../../../constants/publish';
import VTooltipWrapper
    from '../../common/v-tooltip-wrapper.vue';
import ProviderPublishTable
    from '../../common/providers/providerPublishTable.vue';
import ElementPreviewV2
    from '../elements/elementPreviewV2/elementPreviewV2.vue';
import {localizeDate} from "../../../../utils/date";
import ValidityDisplay from "../campaign/validity-display.vue";
import WarningIcon from "../elements/warningIcon.vue";
import { formatTime, getTimeFormat } from '../../../../utils/time';

const allDays = [
    'pdMonday',
    'pdTuesday',
    'pdWednesday',
    'pdThursday',
    'pdFriday',
    'pdSaturday',
    'pdSunday'
];

const numScreensAndScreengroupsToShowInChips = 3;
const numScreensAndScreengroupsToShowInTooltip = 10;
const indexAfterLastScreenOrScreengroupToShowInTooltip = numScreensAndScreengroupsToShowInChips + numScreensAndScreengroupsToShowInTooltip;

export default {
    props: {
        /** @type {ScreensData} */
        screensData: Object,
        /** @type {PublishElement} */
        element: Object,
        value: Number,
        index: Number,
        grid: String,
        /** @type {PublishLayer} */
        active_layer: Object,
        layers_mapped: Object,
        /** @type {'1' | undefined} */
        editPrivilege: String,
        /** @type {'1' | undefined} */
        deletePrivilege: String,
        /** @type {'1' | undefined} */
        approvePrivilege: String,
        elementWidth: Number,
        elementHeight: Number,
        thumbnailSizeNotSmall: Boolean,
        aspectRatio: Number,
        sizeRatio: Number,
        loadingDeletionState: Boolean,
        loadingDeletionStateElementId: Number
    },
    mixins: [bootstrapElement],
    name: 'publishElement',
    components: {
        ValidityDisplay,
        WarningIcon,
        ElementPreviewV2,
        ProviderPublishTable,
        VTooltipWrapper,
        SizeNotMatchingIcon,
        Template,
        TextEllipsisWithTooltip,
        AdvancedThumbnail,
        actions,
        textElement,
        rssfeedElement,
        iframeElement,
        youtubeElement,
        qrcodeElement,
        countdownElement,
        pictureElement,
        videoElement,
        pdfElement,
        pptElement,
        emptyrectangleElement,
        templateElement,
        campaignElement,
        clockElement,
        dayElement,
        weatherElement,
        layerElement,
        esignElement,
        streamingElement,
        elementState,
        widgetElement,
        elementIcon
    },

    data() {
        return {
            t_my_screens: Lang.langGet('My Screens'),
            t_approved: Lang.langGet('Approved'),
            t_not_approved: Lang.langGet('Not approved'),
            t_approve: Lang.langGet('Approve'),
            t_disapprove: Lang.langGet('Disapprove'),
            t_duration: Lang.langGet('Duration'),
            t_m: Lang.langGet('m'),
            t_s: Lang.langGet('s'),
            t_display_runs: Lang.langGet('Runs'),
            t_publish_warning: Lang.langGet('This element will not be published because:'),
            t_approve_missing: Lang.langGet('Approval is missing.'),
            t_screen_screengroup_missing: Lang.langGet('No screens and/or screengroups assigned.'),
            t_local_copy: Lang.langGet('Create Local Copy'),
            t_validity: Lang.langGet('Validity'),
            t_edit_settings: Lang.langGet('Edit Element Settings'),
            t_delete: Lang.langGet('Delete'),
            t_reject: Lang.langGet('Reject'),
            t_settings: Lang.langChoice('Setting|Settings', 2),
            t_duration_element: Lang.langGet('Element duration'),
            t_readonly: Lang.langGet('READ ONLY'),
            t_copy: Lang.langGet('Create copy'),
            t_plays_on: Lang.langGet('Plays on'),
            t_stretch: Lang.langGet('Stretch'),
            t_stretch_frame: Lang.langGet('Stretched to frame-size'),
            t_stretch_aspect_fill: Lang.langGet('Full-Frame with Aspect-Ratio'),
            t_stretch_aspect_fit: Lang.langGet('Stretched with Aspect-Ratio'),
            t_stretch_original: Lang.langGet('Original Size'),
            t_position: Lang.langGet('Alignment'),
            t_position_center: Lang.langGet('Centered'),
            t_position_topleft: Lang.langGet('Top-Left'),
            t_position_topright: Lang.langGet('Top-Right'),
            t_position_bottomright: Lang.langGet('Bottom-Right'),
            t_position_bottomleft: Lang.langGet('Bottom-Left'),
            t_screen_not_allowed: Lang.langGet('You are not allowed to modify this element, because screens / screengroups are missing. You have to get them assigned via usergroups.'),
            t_others: Lang.langGet('others'),
            t_more: Lang.langGet('more'),
            t_props_not_set: Lang.langGet('Required properties have not been set.'),
            inner_element: {
                data: {
                    screens: [],
                    screengroups: [],
                    name: '',
                    valid_time_from: '',
                    valid_time_to: '',
                    valid_from: [],
                    valid_to: [],
                    weekdays: [],
                    type: ''
                },
                deleted_at: null
            },
            inner_screens: null,
            inner_screengroups: null,
            duration_type: 1,
            duration_value: 0,
            previewFrame: {
                width: 1,
                height: 1,
                ratio: 1
            },

            /** @type {PublishState} */
            inner_state: this.element.state,
            eicon_screengroup: '<div class=\'eicon m-r-xs\'><svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"    width="16px" height="16px" viewBox="0 0 16 16" enable-background="new 0 0 16 16" xml:space="preserve">  <rect x="5.5" y="0.281" fill="currentColor" width="5" height="4"/>  <rect y="10" fill="currentColor" width="5" height="4"/>  <rect x="11" y="10" fill="currentColor" width="5" height="4"/>  <rect x="5.5" y="10" fill="currentColor" width="5" height="4"/>  <line fill="none" stroke="currentColor" stroke-miterlimit="10" x1="2.458" y1="7.333" x2="13.458" y2="7.333"/>  <line fill="none" stroke="currentColor" stroke-miterlimit="10" x1="7.958" y1="10.625" x2="7.958" y2="3.625"/>  <line fill="none" stroke="currentColor" stroke-miterlimit="10" x1="2.958" y1="10.625" x2="2.958" y2="7.625"/>  <line fill="none" stroke="currentColor" stroke-miterlimit="10" x1="12.958" y1="10.625" x2="12.958" y2="7.625"/>  </svg></div>',
            eicon_screen: '<div class=\'eicon m-r-xs\'><svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"    width="16px" height="16px" viewBox="0 0 16 16" enable-background="new 0 0 16 16" xml:space="preserve">  <rect x="3.5" y="13.5" fill="currentColor" width="9" height="2"/>  <rect x="6.5" y="11.5" fill="currentColor" width="3" height="2"/>  <rect fill="currentColor" width="16" height="12"/>  <rect x="1.5" y="1" fill="#FFFFFF" width="13" height="9"/>  </svg></div>',
            eicon_user: '<div class=\'eicon m-r-xs\'><svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"    width="13px" height="13px" viewBox="1.5 1.5 13 13" enable-background="new 1.5 1.5 13 13" xml:space="preserve">  <g>   <path fill="currentColor" d="M8,1.5C4.41,1.5,1.5,4.41,1.5,8c0,3.591,2.91,6.5,6.5,6.5s6.5-2.909,6.5-6.5C14.5,4.41,11.59,1.5,8,1.5z     M8,13.5c-3.037,0-5.5-2.462-5.5-5.5c0-3.037,2.463-5.5,5.5-5.5s5.5,2.463,5.5,5.5C13.5,11.038,11.037,13.5,8,13.5z"/>   <circle fill="currentColor" cx="7.825" cy="5.038" r="1.862"/>   <path fill="currentColor" d="M4.042,13.067c0-3.121,1.157-6.168,3.947-6.168c2.79,0,3.425,3.981,3.425,6.168    c-1.73,0.916-2.133,1.119-3.386,1.119C6.074,14.187,4.042,13.067,4.042,13.067z"/>  </g>  </svg></div>',
            elementHover: false,
            timepickerMode: Laravel.timepickerMode,
        };
    },
    computed: {
        min_width_height: function () {
            return Math.min(this.elementWidth, this.elementHeight);
        },
        canBeApproved() {
            return this.approvePrivilege === '1' || this.deletePrivilege === '1' || this.approvePrivilege === '1';
        },
        allMyScreens() {
            if (_.size(this.screensData.screengroupsToScreens[0]) > 0 && this.screensData.all_screengroups[0]['text'] === this.t_my_screens) {
                let allScreens = JSON.parse(JSON.stringify(this.inner_screens));

                if (_.size(this.inner_screengroups) > 0) {
                    let allScreengroups = JSON.parse(JSON.stringify(this.inner_screengroups));

                    _.each(allScreengroups, (screengroup) => {
                        if (_.size(this.screensData.screengroupsToScreens[screengroup]) > 0) {
                            allScreens.push(this.screensData.screengroupsToScreens[screengroup]);
                        }
                    });
                }

                allScreens = _.map(_.flatten(allScreens), (o) => {
                    return parseInt(o);
                });

                if (_.size(this.screensData.screengroupsToScreens[0]) === _.size(_.intersection(_.flatten(allScreens), this.screensData.screengroupsToScreens[0]))) {
                    return true;
                }
            }

            return false;
        },
        basicContentStyles() {
            var width = '100%';
            var height = width;

            return {
                //position: 'absolute',
                width: width,
                height: height,
                position: 'relative',
                overflow: 'hidden'
            };
        },
        /* Could be removed, is used in *Elements only if 'description' is not null, but we are passing null in this component. */
        infoStyles() {
            var left = 4;
            var bottom = 4;
            return {
                left: left + 'px',
                bottom: bottom + 'px'
            };
        },
        elementName() {
            if (this.inner_element.data.name) {
                return _.toString(this.inner_element.data.name);
            }
            let type = this.inner_element.data.type;

            const selectedLayerName = this.layers_mapped[this.inner_element.data.layer];
            let desc = this.inner_element.data.description || (selectedLayerName ? selectedLayerName.text : '');
            desc = (desc !== '') ? ' - ' + desc : '';
            if (type !== '') {
                if (type === 'campaign' && element.dynamic) {
                    type = Lang.langGet('Dynamic Campaign');
                } else {
                    type = elementTypes.titles[type];
                }
            }
            return type + desc;
        },
        default_duration() {
            return Laravel.default_duration;
        },
        canShowMedia() {
            return (this.inner_element.data.type === 'widget' && this.inner_element.data.index_html) || this.inner_element.data.background_image;
        },
        thumbnailStyle() {
            return this.canShowMedia ? {} : {
                cursor: 'unset'
            };
        },
        inner_duration_element() {
            if (this.inner_element.data.type === 'ppt' || this.inner_element.data.type === 'pdf') {
                const page_to = parseInt(!isNaN(this.inner_element.data.page_to) && this.inner_element.data.page_to !== '' ? this.inner_element.data.page_to : this.inner_element.data.page_count);
                const page_from = parseInt(!isNaN(this.inner_element.data.page_from) && this.inner_element.data.page_from !== '' ? this.inner_element.data.page_from : 1);
                const interval = parseInt(!isNaN(this.inner_element.data.interval) && this.inner_element.data.interval !== '' ? this.inner_element.data.interval : 11);

                if (interval && page_from && page_to) {
                    return Math.max(interval * (page_to - page_from + 1), 0) ?? '?';
                } else {
                    return '?';
                }
            } else {
                if (this.inner_element.data.duration_original === 'unknown' || !this.inner_element.data.duration_original) {
                    return '?';
                } else {
                    return this.inner_element.data.duration_original;
                }
            }
        },
        elementScreensAndScreengroups() {
            const value = [];
            const allScreensId = this.screensData.all_screengroups?.find(screengroup => screengroup.text == 'All Screens')?.value ?? null;
            const myScreensId = this.screensData.all_screengroups?.find(screengroup => screengroup.text == this.t_my_screens)?.value ?? null;

            this.inner_element.data.screens.forEach(screenId => value.push(`screen-${screenId}`));
            this.inner_element.data.screengroups.forEach(groupId => {
                const groupName = `group-${groupId}`;
                if (groupId === allScreensId || groupId === myScreensId) {
                    value.unshift(groupName);
                } else {
                    value.push(groupName);
                }
            });
            return value;
        },
        cappedNamedElementScreensAndScreengroups() {
            let endIndex = numScreensAndScreengroupsToShowInChips;
            // If number of screens is exactly such that the "+ other screens" would read "+1 screen"
            if (this.numScreensAndScreengroupsElementPlaysOn == (numScreensAndScreengroupsToShowInChips + 1)) {
                endIndex += 1;
            }
            return this.prepareAndLimitElementScreensAndScreengroups(0, endIndex);
        },
        cappedRestNamedElementScreensAndScreengroups() {
            return this.prepareAndLimitElementScreensAndScreengroups(numScreensAndScreengroupsToShowInChips, indexAfterLastScreenOrScreengroupToShowInTooltip);
        },
        numOfMoreScreensOrScreengroups() {
            return this.elementScreensAndScreengroups.length - indexAfterLastScreenOrScreengroupToShowInTooltip;
        },
        approved: {
            get() {
                return this.inner_element.data.approved;
            },
            set(newVal) {
                if (newVal) {
                    this.approve();
                } else {
                    this.disapprove();
                }
            }
        },
        elementActive() {
            return this.value === this.index;
        },
        elementMarkedForDeletion() {
            return this.inner_element.deleted_at != null;
        },
        elementDuration() {
            let duration = 0;
            let runs_text = '';

            if (this.hasDisplayRuns() && this.inner_element.data.duration_type === 2) {
                let runs = this.inner_element.data.display_runs;
                if (isNaN(this.inner_duration_element)) {
                    duration = '';
                    runs_text = `${runs} ${Lang.langChoice('Run|Runs', runs)}`;
                } else {
                    duration += parseInt(this.inner_duration_element) * parseInt(this.inner_element.data.display_runs) || 0;
                    runs_text = ` (${runs} ${Lang.langChoice('Run|Runs', runs)})`;
                }

            } else if (this.inner_element.data.duration_type === 0) {
                duration += parseInt(this.inner_element.data.duration);
            } else {
                duration += this.default_duration;
            }

            if (duration > 60) {
                return `${Math.floor(duration / 60)}${this.t_m} ${duration % 60}${this.t_s}${runs_text}`;
            } else {
                return `${duration ? `${duration}${this.t_s}` : ''}${runs_text}`;
            }
        },
        elementLoops() {
            return this.inner_element.data.loop > 1 ? `[1/${this.inner_element.data.loop}]` : null;
        },
        validityDate() {
            const {valid_from, valid_to, valid_from_placeholder, valid_to_placeholder} = this.inner_element.data;

            function transformDate(date) {
                if (!date) return undefined;
                if (date.length === 0) return undefined;
                return date;
            }
            const dateFrom = transformDate(valid_from) || valid_from_placeholder;
            const dateTo = transformDate(valid_to) || valid_to_placeholder;

            if (!dateFrom && !dateTo) return Lang.langGet('always valid');

            if (dateFrom && dateTo) {
                return `${localizeDate(dateFrom)} - ${localizeDate(dateTo)}`;
            }

            if (!dateFrom) {
                return `${Lang.langGet('valid until')} ${localizeDate(dateTo)}`;
            }

            return `${Lang.langGet('valid from')} ${localizeDate(dateFrom)}`;
        },
        validityTime() {
            if (typeof this.inner_element.data.valid_time_from !== 'string') {
                this.inner_element.data.valid_time_from = '';
            }
            if (typeof this.inner_element.data.valid_time_to !== 'string') {
                this.inner_element.data.valid_time_to = '';
            }
            let timeFrom = this.inner_element.data.valid_time_from.split(':');
            let timeTo = this.inner_element.data.valid_time_to.split(':');

            if (timeFrom.length !== 2) {
                timeFrom = {HH: '00', mm: '00'};
            } else {
                timeFrom = {HH: timeFrom[0], mm: timeFrom[1]};
            }

            if (timeTo.length !== 2) {
                timeTo = {HH: '00', mm: '00'};
            } else {
                timeTo = {HH: timeTo[0], mm: timeTo[1]};
            }


            if ((timeFrom.HH === '00' && timeFrom.mm === '00' && timeTo.HH === '00' && timeTo.mm === '00') ||
                ((timeFrom.HH === null || timeFrom.mm === null) && (timeTo.HH === null || timeTo.mm === null))
            ) {
                return Lang.langGet('full-time');
            } else {
                if (timeFrom.HH === null || timeFrom.mm === null || (timeFrom.HH === '00' && timeFrom.mm === '00')) {
                    return [
                        Lang.langGet('to'),
                        formatTime({
                            h: +timeTo.HH,
                            m: +timeTo.mm
                        }, getTimeFormat(this.timepickerMode))
                    ].join(' ');
                } else if (timeTo.HH === null || timeTo.mm === null || (timeTo.HH === '00' && timeTo.mm === '00')) {
                    return [
                        Lang.langGet('from'),
                        formatTime({
                            h: +timeFrom.HH,
                            m: +timeFrom.mm
                        }, getTimeFormat(this.timepickerMode))
                    ].join(' ');
                } else {
                    return [
                        Lang.langGet('between'),
                        formatTime({
                            h: +timeFrom.HH,
                            m: +timeFrom.mm
                        }, getTimeFormat(this.timepickerMode)),
                        Lang.langGet('and'),
                        formatTime({
                            h: +timeTo.HH,
                            m: +timeTo.mm
                        }, getTimeFormat(this.timepickerMode))
                    ].join(' ');
                }
            }
        },
        validityWeekdays() {
            const days = this.inner_element.data.weekdays;

            if (days?.length > 0) {
                if (days.length === 7) {
                    return Lang.langGet('on no weekday(!)');
                } else if (days.length <= 3) {
                    return `${Lang.langGet('not on')} ${days.map(day => Lang.langGet(day.substring(2))).join(', ')}`;
                } else {
                    return `${Lang.langGet('only on')} ${allDays.filter(weekday => !days.includes(weekday)).map(day => Lang.langGet(day.substring(2))).join(', ')}`;
                }
            } else return null;
        },
        numScreensAndScreengroupsElementPlaysOn() {
            return (this.inner_element.data?.screens?.length ?? 0) + (this.inner_element.data?.screengroups?.length ?? 0);
        },
        noScreensOrScreengroupsAssigned() {
            return this.elementScreensAndScreengroups?.length < 1;
        },
        showOtherScreensChip() {
            return this.numScreensAndScreengroupsElementPlaysOn > (numScreensAndScreengroupsToShowInChips + 1);
        },
        numOtherScreensOrScreengroupsAssigned() {
            return this.elementScreensAndScreengroups.length - numScreensAndScreengroupsToShowInChips;
        },
        readonly() {
            return this.inner_element.data.readonly;
        },
        sizeNotMatching() {
            if (!this.element || !this.element.data) {
                return false;
            }

            const data = this.element.data;

            return elementSizeDifferentFromLayerSize(data.width, data.height, this.active_layer.width, this.active_layer.height);
        },
        propsNotSet() {
            return this.layerNotSet||this.urlNotSet||this.eSignNotSet;
        },
        eSignNotSet() {
            if (!this.element || !this.element.data) {
                return false;
            }
            if (this.element.data.type !== 'esign') return false;

            return !(this.element.data.esign_id && (this.element.data.esign_style!==""));
        },
        layerNotSet() {
            if (!this.element || !this.element.data) {
                return false;
            }

            if (this.element.data.type !== 'layer') return false;

            return !this.element.data.layer;
        },
        urlNotSet() {
            if (!this.element || !this.element.data) {
                return false;
            }

            const {type, iframe_url, rssfeed_url, url} = this.element.data;

            if (type !== 'iframe' && type !== 'streaming' && type !== 'rssfeed') return false;

            if (type === 'iframe') return !iframe_url;
            if (type === 'rssfeed') return !rssfeed_url
            return !url;
        },
        approveSwitchDisabled() {
            return this.readonly || !this.approvePrivilege || this.elementMarkedForDeletion;
        },
        approveSwitchAriaLabel() {
            return this.approved ? this.t_disapprove : this.t_approve;
        },
        elementDeletionInProgress() {
            return this.loadingDeletionStateElementId == this.index
        }
    },
    watch: {
        element: {
            handler() {
                this.inner_element = this.element;
                this.inner_screens = this.inner_element.data.screens;
                this.inner_screengroups = this.inner_element.data.screengroups;

                this.duration_value = this.inner_element.data.duration;

                this.inner_state = this.inner_element.state;
            },
            deep: true
        },
        active_layer: function () {
            this.calculatePreviewFrame();
        }
    },
    methods: {
        getDescriptionForElement,
        checkValidity,
        t_element_error_info() {
            return t_element_error_info;
        },
        calculatePreviewFrame() {
            let maxHeight = 150;
            let maxWidth = 200;

            let ratio = null,
                thumbWidth = null,
                thumbHeight = null;

            ratio = this.active_layer.height / this.active_layer.width;

            thumbWidth = maxWidth;
            thumbHeight = maxWidth * ratio;

            if (thumbHeight > maxHeight) {
                thumbWidth = maxHeight / ratio;
                thumbHeight = maxHeight;
            }

            this.previewFrame = {
                width: thumbWidth,
                height: thumbHeight,
                ratio: thumbWidth / this.active_layer.width
            };
        },
        screenText(screenId) {
            const found = this.screensData.all_screens?.find(screen => screen.value == screenId);
            return found?.text ?? '???';
        },
        screengroupText(screengroupId) {
            const found = this.screensData.all_screengroups?.find(screengroup => screengroup.value == screengroupId);
            return found?.text ?? '???';
        },
        approve() {
            EaseEventBus.fire('approveSelectedElement', this.index);
        },
        disapprove() {
            EaseEventBus.fire('rejectSelectedElement', this.index);
        },
        scrollToScreenSettings() {
            this.tap();
            EaseEventBus.fire('scrollToScreenSettings');
        },
        showScreensModal() {
            if (!this.elementActive) {
                this.tap();
            }
            if (!this.readonly) {
                EaseEventBus.fire('showModal', 'screens-modal');
            }
        },
        showMedia() {
            const element = this.inner_element;
            if (element.data.type === 'widget' && element.data.index_html) {
                let url = element.data.index_html;

                this.setActive();
                this.$nextTick(() => {
                    EaseEventBus.fire('showMedia_WidgetAddQuery', {
                        mime_type: 'text/html',
                        original: url,
                        widget_id: element.data.id,
                        display_name: element.data.name,
                        previewFrame: _.extend({}, this.previewFrame)
                    });
                });
            } else if (element.data.background_image) {
                let url = '';
                let mime = 'image/jpg';

                if (element.data.type === 'video') {
                    mime = 'video/mp4';
                    url = element.data.background_image.replace('/thumb', '').replace('.jpg', '');
                } else if (element.data.type === 'campaign') {
                    url = element.data.background_image.replace('/thumb', '/original');
                } else if (element.data.type === 'widget') {
                    url = element.data.background_image;
                } else {
                    url = element.data.background_image.replace('/thumb', '');
                }

                EaseEventBus.fire('showMedia', {
                    mime_type: mime,
                    original: url,
                    display_name: element.data.name
                });
            }
        },
        setActive() {
            if (!this.elementActive) {
                this.tap();
                EaseEventBus.fire('settingsActive');
            }
        },
        hasDisplayRuns() {
            let type = this.inner_element.data.type;
            return _.indexOf(['video', 'ppt', 'pdf', 'campaign', 'template', 'widget', 'layer', 'youtube', 'rssfeed'], type) !== -1;
        },
        prepareAndLimitElementScreensAndScreengroups(start = 0, end) {
            const screens = this.elementScreensAndScreengroups.slice(start, end).map(screenOrScreengroup => {
                const [entryType, entryId] = screenOrScreengroup.split('-');
                const typeofScreen = entryType.startsWith('s');
                const name = typeofScreen ? this.screenText(entryId) : this.screengroupText(entryId);
                return {
                    text: name,
                    icon: typeofScreen ? 'fa-display' : (name == 'All Screens' ? 'fa-display-medical' : 'fa-display-code')
                };
            });

            return screens.filter(screen => screen.text !== '???');
        }
    },
    created() {
        EaseEventBus.listen('timepickerModeUpdated', timepickerMode => {
            this.timepickerMode = timepickerMode;
        });
    },
    mounted() {
        this.inner_element = this.element;

        this.calculatePreviewFrame();
    }
};
</script>
